import { Item, Collection, qtyNum, join } from "./collection_base"
import { strictNum } from "#lib/equipment_cost"
import DataLoader from "#lib/data_loader"

// Chips — a JSON array of { slug, skill?, rank? } (display-focused). A family chip picks
// a skill + rank (named <select>s); a single-skill chip carries a hidden skill; effect
// chips neither. Cost = costPerRank × rank. `active` is PLAY-STATE — it lives in the
// `state` blob (owned by the play-state controller), NOT here: the chip's active toggle is
// NOT a named/bound control, so it never enters the stash and entry() can't see it — it
// can't leak into the chips blob (§4 of docs/cp2020-show-layer.md — leak-impossible).

export class ChipItem extends Item {
  // Fill self (edit): fill the varying skill/rank SLOT by chip kind, restoring the stored
  // skill/rank (an add has an empty entry, so picks default to blank/1). Moved here from
  // ChipsCollection#fillItem — same body, on the item via this.builder(). Injects the
  // skill/rank named controls into the slot; they exist before afterFill's changed() reads
  // the stash. `active` is NOT filled here — it's play-state, applied later by applyActive.
  async fill(entry) {
    const b = this.builder()
    const [c, families, skills] = await Promise.all([
      DataLoader.systemGet("chipware").then((cat) => cat[entry.slug]),
      DataLoader.systemGet("chipware-skills"),
      DataLoader.systemGet("skills"),
    ])
    if (!c) return
    const fam = families[entry.slug]
    const familyPick = fam?.candidates?.length
    b.text("name", c.name || "(unknown)")
    b.text("identity", join(c.type, c.subtype || null))
    b.text("description", c.description)
    // Cost basis: per-rank price for a family chip, else the fixed catalog cost.
    b.field("cost_per_rank", familyPick ? c.costPerRank : c.cost)
    const slot = b.el.querySelector("[data-chip-slot]")
    if (familyPick) {
      const skillValue = entry.skill || ""
      const rankValue = entry.rank || 1
      slot?.append(skillPick(fam.candidates, skills, skillValue), rankPick(rankValue))
      // The skill/rank selects are INJECTED (not builder-filled), but they're SERIALIZED —
      // so bind them into the stash like any other control (queryable + user picks flow to
      // the stash via the fromDom listener). bind() also seeds via set() below.
      this.bindInjected("skill", "control", skillValue)
      this.bindInjected("rank", "control", rankValue)
    } else if (entry.skill || c.skill) {
      const skillValue = entry.skill || c.skill // single-skill; effect chips have none
      slot?.append(hiddenSkill(skillValue))
      this.bindInjected("skill", "control", skillValue)
    }
  }

  // Bind a dynamically-injected named control into the stash (the builder can't, since it
  // ran before the control existed): register the binding + seed the stash value + wire the
  // fromDom listener, by re-querying the control now that it's in the DOM.
  bindInjected(name, kind, value) {
    this.bind(name, this.element.querySelector(`[name='${name}']`), kind)
    this.set(name, value)
  }

  // Fill self (show) — the lean play card: name + rank only (no cost/identity/description/
  // skill; those are authoring concerns). `active` is painted separately by applyActive.
  // The show template flags data-fill="show" so the base dispatches here.
  async fillShow(entry) {
    const b = this.builder()
    const c = (await DataLoader.systemGet("chipware"))[entry.slug] || {}
    b.text("name", c.name || "(unknown)")
    if (entry.rank) b.text("rank", `+${entry.rank}`)
  }

  // Blob entry from the STASH: { slug, skill?, rank? }. cost_per_rank is a cost-calc control
  // (never serialized — not in the list). skill omitted when unpicked; rank coerced to a
  // number (it's a fixed-option select, not free text — a real numeric). `active` is play-
  // state (unnamed, never stashed), so it can't leak here. No id (chips key on slug).
  entry() {
    const e = { slug: this.slugValue || "" }
    const skill = this.get("skill")
    if (skill) e.skill = skill
    if (this.get("rank") != null) e.rank = Number(this.get("rank"))
    return e
  }

  cost() {
    return strictNum(this.get("cost_per_rank")) * qtyNum(this.get("rank"))
  }

  // --- play-state (active) — the show-page toggle checkbox. NOT serialized by this item
  // (it's unnamed/unbound, so the stash + entry() never see it); the play-state controller reads it
  // via the chips collection and writes it to `state`. The collection PAINTS active state
  // on the DOM directly (applyActive, a connect-race-safe census), so there's no item-
  // level setter — the item only ANSWERS isActive() and announces toggles. ---
  activeEl() { return this.element.querySelector("[data-active-toggle]") }
  isActive() { return !!this.activeEl()?.checked }

  // The user changed the checkbox (show page). Announce so the play-state controller
  // re-serializes `state`. The checkbox already reflects the new state (native change);
  // this just notifies. Does NOT touch the item's own equipment serialize.
  toggleActive() {
    this.dispatch("play-state-changed", { prefix: "cp2020", bubbles: true })
  }
}

// Chips carry NO id (slug is the key) — the one area without a hidden id control.
export class ChipsCollection extends Collection {
  static CATALOGS = ["chipware", "chipware-skills", "skills"]

  // Announce (AFTER hydrate — our items are actually rendered) that we're ready, so the
  // play-state controller can PUSH the persisted active-state onto our items (applyActive).
  // Chips' own readiness depends ONLY on its items existing — NOT on the state controller.
  // super.connect() kicks hydrate (sets _hydrated); we wait for it so getAll() is complete
  // when the state controller pushes, then signal — the active-state owner applies.
  connect() {
    super.connect()
    this._hydrated?.then(() => this.dispatch("chips-ready", { prefix: "cp2020", bubbles: true }))
  }

  async newEntry(slug) {
    const c = (await DataLoader.systemGet("chipware"))[slug]
    if (!c) return null
    return { slug } // no active — play-state, not an equipment field
  }

  // --- play-state interface (the play-state controller composes this collection) -------
  // PUSH-ON-LOAD: the state controller hands us the persisted active slugs; we paint each
  // item's checkbox. AWAITS OWN READINESS FIRST (gate A) — called on load, outlet-connect,
  // and chips-ready, only some of which coincide with a hydrated set; awaiting means it can't
  // run too early (zero-cost on the chips-ready path, which already fires post-hydrate).
  // DOM census (slug-value + active-toggle), not getAll(). Idempotent.
  async applyActive(activeSlugs) {
    await this.whenReady()
    const active = new Set(activeSlugs || [])
    const slugAttr = `data-${this.itemIdentity}-slug-value`
    for (const el of this.element.querySelectorAll(`[${slugAttr}]`)) {
      const box = el.querySelector("[data-active-toggle]")
      if (box) box.checked = active.has(el.getAttribute(slugAttr))
    }
  }

  // PULL-ON-SAVE: the state controller asks "what's active now?" when it re-serializes
  // (after a user toggle). Chips are the authority on their own current toggle state.
  activeKeys() {
    return this.getAll().filter((i) => i.isActive()).map((i) => i.slugValue)
  }
}

// --- skill / rank pick controls (injected into a chip's slot) --------------------
function pickWrap(field, label, select) {
  const wrap = document.createElement("div")
  wrap.className = "mt-1"
  const lbl = document.createElement("label")
  lbl.className = "text-xs text-gray-500 block"
  lbl.textContent = label
  select.className = "w-full text-xs rounded border border-gray-300 dark:border-gray-600 bg-transparent px-1 py-0.5"
  select.name = field
  wrap.append(lbl, select)
  return wrap
}

function skillPick(candidates, skills, picked) {
  const select = document.createElement("select")
  select.add(new Option("(pick a skill)", ""))
  candidates
    .map((cslug) => [cslug, skillLabel(skills[cslug], cslug)])
    .sort((a, b) => a[1].localeCompare(b[1]))
    .forEach(([cslug, cname]) => select.add(new Option(cname, cslug, false, cslug === picked)))
  return pickWrap("skill", "Skill", select)
}

function rankPick(picked) {
  const select = document.createElement("select")
  for (const r of [1, 2, 3]) select.add(new Option(`+${r}`, String(r), false, r === picked))
  return pickWrap("rank", "Rank", select)
}

function hiddenSkill(value) {
  const input = document.createElement("input")
  input.type = "hidden"
  input.name = "skill"
  input.value = value
  return input
}

function skillLabel(skill, slug) {
  if (!skill) return slug
  return skill.option ? `${skill.name} (${skill.option})` : skill.name
}
